// Marketer (משווק) sales — the screen side of services/marketer.py.
//
// A sale is a marketer sale when the opportunity's ACCOUNT is a marketer (Type = 'Reseller'),
// decided by the server (GET /api/dealer/opportunity/{id}/eligibility) and only while the
// `dealer_sales_enabled` flag is on. Owner decisions (DEALER-SALES-DESIGN-REV-2, ADR-R2-12):
//   * the standard pricebook only (the RESELLER pricebook is a different, unchanged flow)
//   * the seller types the agreed price, INCLUDING VAT (D18), and it must be > 0
//   * the personal price-range clamp does not apply (D17)
// The server enforces "> 0" too; this file only makes the screen say it first.
import config from '../config/config';
import { authHeaders } from './session';

const API_BASE = config.api.baseUrl;

export const MARKETER_PRICE_ERROR = 'בעסקת משווק המחיר חייב להיות גדול מ-0 (המחיר כולל מע"מ)';

const OFF = { active: false, enabled: false, isMarketer: false, accountName: '' };

/** {active, enabled, isMarketer, accountName}. Any failure means "not a marketer sale". */
export async function fetchMarketerContext(opportunityId, fetchImpl = fetch) {
  if (!opportunityId) return OFF;
  try {
    const r = await fetchImpl(`${API_BASE}/api/dealer/opportunity/${encodeURIComponent(opportunityId)}/eligibility`,
      { headers: { ...authHeaders() } });
    if (!r.ok) return OFF;
    const d = await r.json();
    const enabled = !!d.enabled;
    const isMarketer = !!d.is_marketer;
    return { active: enabled && isMarketer, enabled, isMarketer, accountName: d.account_name || '' };
  } catch (e) {
    return OFF;
  }
}

const BOOT_OFF = { enabled: false, isDealerManager: false, canScheduleDeliveries: false };

/** {enabled, isDealerManager, canScheduleDeliveries} for the signed-in user. Any failure = all off. */
export async function fetchDealerBootstrap(fetchImpl = fetch) {
  try {
    const r = await fetchImpl(`${API_BASE}/api/dealer/bootstrap`, { headers: { ...authHeaders() } });
    if (!r.ok) return BOOT_OFF;
    const d = await r.json();
    const enabled = !!d.enabled;
    return {
      enabled,
      isDealerManager: enabled && !!d.is_dealer_manager,
      canScheduleDeliveries: enabled && !!d.can_schedule_deliveries,
    };
  } catch (e) {
    return BOOT_OFF;
  }
}

/** {Product2Id: {price, date}} — the marketer's last agreed price per product. A hint only. */
export async function fetchMarketerLastPrices(opportunityId, fetchImpl = fetch) {
  if (!opportunityId) return {};
  try {
    const r = await fetchImpl(
      `${API_BASE}/api/dealer/opportunity/${encodeURIComponent(opportunityId)}/marketer-last-prices`,
      { headers: { ...authHeaders() } });
    if (!r.ok) return {};
    const d = await r.json();
    return d.prices || {};
  } catch (e) {
    return {};
  }
}

export function isStandardPricebook(pb) {
  if (!pb) return false;
  const s = pb.IsStandard;
  if (s === true || s === 1 || s === '1' || s === 'true') return true;
  return String(pb.Name || '').trim().toUpperCase() === 'STANDARD PRICE BOOK';
}

/** The pricebooks a marketer sale may use: the standard one only. */
export function pricebooksForMarketer(pricebooks) {
  return (pricebooks || []).filter(isStandardPricebook);
}

/** Selected products whose price breaks the rule. Every line, the inline filter included: a
 *  marketer sale gets no automatic free filter (owner 2026-10-09), so one the seller adds is
 *  priced like anything else. */
export function marketerPriceViolations(selectedProducts) {
  return (selectedProducts || []).filter(p => !(Number(p.customPrice) > 0));
}

/** The hint under the price field, or '' when there is no earlier price. */
export function lastPriceHint(lastPrices, productId) {
  const lp = lastPrices && lastPrices[productId];
  if (!lp || !(Number(lp.price) > 0)) return '';
  const [y, m, d] = String(lp.date || '').split('-');
  const date = y && m && d ? ` (${d}/${m}/${y})` : '';
  return `מחיר אחרון למשווק: ₪${Number(lp.price).toLocaleString('he-IL')}${date}`;
}
